import { Controller } from '@hotwired/stimulus';
import AutoNumeric from 'autonumeric';
import { Helpers } from '../helpers';
import * as bootstrap from 'bootstrap';

export default class InvestFormController extends Controller {
  static targets = [
    'subscriptionAmount',
    'feesAmount',
    'containerFieldsErrors',
    'fieldsErrors',
    'subscriptionAmountDivs',
    'feesAmountDivs',
    'totalAmountDivs',
    'modalTotal',
    'submitButton',
    'insufficientBalance',
    'subscribeModal',
    'provisionalBalance',
  ];

  static values = {
    insufficientBalanceMessage: String,
    noAmountMessage: String,
    projectId: String,
    profileBalance: Number,
    max_invest_recommendation: Number,
    user_may_have_warning: Boolean,
    rangeOverflowMessage: String,
    minAmount: Number,
    maxAmount: Number,
    stepAmount: Number,
  };

  connect() {
    this.computeFeesAndTotal();
  }

  #getAmount() {
    try {
      const autoNumericInstance = AutoNumeric.getAutoNumericElement(this.subscriptionAmountTarget);

      return autoNumericInstance.getNumber();
    } catch {
      return parseFloat(this.subscriptionAmountTarget.value) || null;
    }
  }

  // Compute fees and total through backend
  computeFeesAndTotal() {
    const amount = this.#getAmount();
    const profileBalance = parseFloat(this.profileBalanceValue);

    // Check if the amount is valid
    this.checkAmountValidity(amount);

    // Display amount
    this.modalTotalTarget.textContent =
      Helpers.numberToCurrency(amount, 2);

    this.provisionalBalanceTarget.textContent =
      Helpers.numberToCurrency(profileBalance - amount, 2);

    this.updateButtonState(amount, profileBalance);
  }

  checkAmountValidity(amount) {
    if (this.hasInsufficientBalanceTarget) {
      this.insufficientBalanceTarget.classList.add('d-none');
    }

    const profileBalance = parseFloat(this.profileBalanceValue);
    const emptyAmount = amount == null;
    const outOfRange = this.#isOutOfRange(amount);

    const hasError = outOfRange || emptyAmount || amount > profileBalance;

    this.containerFieldsErrorsTarget.classList.toggle('d-none', !hasError);

    switch (true) {
      case outOfRange:
        this.fieldsErrorsTarget.textContent = this.rangeOverflowMessageValue;
        return this.triggerError();
      case emptyAmount:
        this.fieldsErrorsTarget.textContent = this.noAmountMessageValue;
        return this.triggerError();
      case amount > profileBalance:
        this.fieldsErrorsTarget.innerHTML = this.insufficientBalanceMessageValue;
        return this.triggerError();
      default:
        this.submitButtonTarget.classList.remove('disabled');
        this.fieldsErrorsTarget.textContent = '';
        this.subscriptionAmountTarget.classList.remove('is-invalid');
        return true;
    }
  }

  #isOutOfRange(amount) {
    if (amount == null) return false;
    if (this.hasMinAmountValue && amount < this.minAmountValue) return true;
    if (this.hasMaxAmountValue && amount > this.maxAmountValue) return true;
    if (!this.hasStepAmountValue || this.stepAmountValue <= 0) {
      return false;
    }

    const min = this.hasMinAmountValue ? this.minAmountValue : 0;
    const remainder = (amount - min) % this.stepAmountValue;

    return Math.abs(remainder) > 0;
  }

  updateAmounts(container, amount) {
    const finalAmount =
      amount === 0 || isNaN(amount)
        ? 'CHF -,--'
        : Helpers.numberToCurrency(amount, 2);

    container.forEach((element) => {
      element.textContent = finalAmount;
    });
  }

  triggerError() {
    this.submitButtonTarget.classList.add('disabled');
    this.subscriptionAmountTarget.classList.add('is-invalid');
    return false;
  }

  validateAndShowSubscriptionModal(event) {
    event.preventDefault();
    const amount = this.#getAmount();
    const maxInvestRecommendation = this.maxInvestRecommendationValue;

    if (!this.checkAmountValidity(amount)) return;

    this.showModal(this.subscribeModalTarget);
  }

  preventAutoSubmit(event) {
    event.preventDefault();
    this.validateAndShowSubscriptionModal();
  }

  showModal(modalTarget) {
    const modal = new bootstrap.Modal(modalTarget);
    modal.show();
  }

  updateButtonState(amount, profileBalance) {
    const isInvalid = !amount || amount <= 0 || amount > profileBalance;
    this.submitButtonTarget.disabled = isInvalid;
  }
}
